/**
 * React hooks для управления фильтрами заказов
 * Основано на логике из Бот/miniapp/frontend/src/components/OrderFilters.js
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { filtersApi } from '../api/filtersApi'
import type { 
  OrderFiltersState, 
  ProcessingType, 
  FilterCallbacks,
  FiltersData
} from '../../types/filters'
import { logger } from '../utils/logger'

const EXCLUSIONS_STORAGE_KEY = 'excludedProcessingTypes'



/**
 * Hook для управления состоянием фильтров
 */
export const useOrderFiltersState = (callbacks?: FilterCallbacks) => {
  // Ref для callbacks чтобы избежать бесконечного цикла в useEffect
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  const [filtersState, setFiltersState] = useState<OrderFiltersState>({
    selectedProcessingTypes: [],
    selectedSort: 'newest',
    excludedTypes: [],
    isExpanded: false, // Свернуты по умолчанию
    activeTab: 'filters'
  })
  const [isInitialized, setIsInitialized] = useState(false)

  // Загружаем исключения из localStorage при инициализации (только исключения локально)
  useEffect(() => {
    const loadExcludedTypes = () => {
      try {
        const saved = localStorage.getItem(EXCLUSIONS_STORAGE_KEY)
        if (saved) {
          const excludedTypes = JSON.parse(saved)
          if (Array.isArray(excludedTypes)) {
            setFiltersState(prev => ({ ...prev, excludedTypes }))
          }
        }
      } catch (error) {
        logger.error('Ошибка загрузки исключений:', error)
      }
    }

    loadExcludedTypes()
  }, [])

  // Загружаем сохраненные фильтры с сервера
  useEffect(() => {
    const loadSavedFilters = async () => {
      try {
        const response = await filtersApi.getQuickFilters()
        setFiltersState(prev => {
          const newState = {
            ...prev,
            selectedProcessingTypes: response.quick_filters || []
          }
          
          // Вызываем callback только если есть сохраненные фильтры
          if (response.quick_filters && response.quick_filters.length > 0 && callbacksRef.current?.onFiltersChange) {
            setTimeout(() => {
              callbacksRef.current?.onFiltersChange?.(newState)
            }, 0)
          }
          
          return newState
        })
      } catch (error: any) {
        logger.warn('⚠️ Ошибка загрузки сохраненных фильтров:', error)
        // При ошибке просто продолжаем без сохраненных фильтров
      } finally {
        setIsInitialized(true)
      }
    }

    loadSavedFilters()
  }, []) // Убираем callbacks из зависимостей - загружаем только один раз!

  // Сохранение исключений в localStorage
  const saveExcludedTypes = useCallback((excludedTypes: string[]) => {
    try {
      localStorage.setItem(EXCLUSIONS_STORAGE_KEY, JSON.stringify(excludedTypes))
    } catch (error) {
      logger.error('Ошибка сохранения исключений:', error)
    }
  }, [])

  // Сохранение быстрых фильтров только на сервере
  const saveQuickFilters = useCallback(async (selectedProcessingTypes: string[]) => {
    try {
      await filtersApi.syncQuickFilters({
        quick_filters: selectedProcessingTypes
      })
      logger.log('✅ Фильтры сохранены на сервере:', selectedProcessingTypes)
    } catch (error) {
      logger.error('❌ Ошибка сохранения фильтров на сервере:', error)
      callbacksRef.current?.onError?.('Ошибка сохранения фильтров на сервере')
    }
  }, [])

  // Обновление фильтров
  const updateFilters = useCallback((updates: Partial<OrderFiltersState>) => {
    setFiltersState(prev => {
      const newState = { ...prev, ...updates }
      
      // Сохраняем исключения в localStorage при их изменении
      if (updates.excludedTypes !== undefined) {
        saveExcludedTypes(newState.excludedTypes)
      }
      
      // Сохраняем быстрые фильтры на сервере при их изменении
      if (updates.selectedProcessingTypes !== undefined) {
        saveQuickFilters(newState.selectedProcessingTypes)
      }
      
      // Вызываем callback ТОЛЬКО при изменении фильтров, НЕ при UI изменениях
      // И только после инициализации
            const shouldTriggerCallback =
        isInitialized &&
        (updates.selectedProcessingTypes !== undefined ||
         updates.excludedTypes !== undefined ||
         updates.selectedSort !== undefined)

      if (shouldTriggerCallback) {
        callbacksRef.current?.onFiltersChange?.(newState)
      }

      return newState
    })
  }, [saveExcludedTypes, saveQuickFilters, isInitialized])

  // Переключение типа обработки в фильтрах
  const toggleProcessingType = useCallback((typeValue: string) => {
    setFiltersState(prev => {
      const isSelected = prev.selectedProcessingTypes.includes(typeValue)
      const newSelectedTypes = isSelected
        ? prev.selectedProcessingTypes.filter(t => t !== typeValue)
        : [...prev.selectedProcessingTypes, typeValue]
      
      // Если тип добавляется в фильтры, убираем его из исключений
      const newExcludedTypes = !isSelected && prev.excludedTypes.includes(typeValue)
        ? prev.excludedTypes.filter(t => t !== typeValue)
        : prev.excludedTypes
      
      const newState = { 
        ...prev, 
        selectedProcessingTypes: newSelectedTypes,
        excludedTypes: newExcludedTypes
      }
      
      // Сохраняем на сервере
      saveQuickFilters(newSelectedTypes)
      
      // Сохраняем исключения если они изменились
      if (newExcludedTypes !== prev.excludedTypes) {
        saveExcludedTypes(newExcludedTypes)
      }
      
      if (isInitialized) {
        callbacksRef.current?.onFiltersChange?.(newState)
      }
      
      return newState
    })
  }, [saveQuickFilters, saveExcludedTypes, isInitialized])

  // Переключение исключенного типа
  const toggleExcludedType = useCallback((typeValue: string) => {
    setFiltersState(prev => {
      const isExcluded = prev.excludedTypes.includes(typeValue)
      const newExcludedTypes = isExcluded
        ? prev.excludedTypes.filter(t => t !== typeValue)
        : [...prev.excludedTypes, typeValue]
      
      const newState = { ...prev, excludedTypes: newExcludedTypes }
      
      // Сохраняем в localStorage
      saveExcludedTypes(newExcludedTypes)
      if (isInitialized) {
        callbacks?.onFiltersChange?.(newState)
      }
      
      return newState
    })
  }, [saveExcludedTypes, callbacks, isInitialized])

  // Сброс фильтров
  const resetFilters = useCallback(() => {
    const newState = {
      ...filtersState,
      selectedProcessingTypes: [],
      selectedSort: 'newest' as const
    }
    
    setFiltersState(newState)
    saveQuickFilters([])
    if (isInitialized) {
      callbacks?.onFiltersChange?.(newState)
    }
  }, [filtersState, saveQuickFilters, callbacks, isInitialized])

  // Сброс исключений
  const resetExclusions = useCallback(() => {
    const newState = { ...filtersState, excludedTypes: [] }
    setFiltersState(newState)
    saveExcludedTypes([])
    if (isInitialized) {
      callbacks?.onFiltersChange?.(newState)
    }
  }, [filtersState, saveExcludedTypes, callbacks, isInitialized])

  return {
    filtersState,
    updateFilters,
    toggleProcessingType,
    toggleExcludedType,
    resetFilters,
    resetExclusions
  }
}
